import { useState } from 'react';
import { Container, Card, Button, Alert, Row, Col } from 'react-bootstrap';
import { useShipping } from '../../../apps/hooks/shipping/useShipping';
import { useGlobalShippingSettings } from '../../../apps/hooks/shipping/useGlobalShippingSettings';
import type { ShippingPolicy, GlobalShippingSettings } from '../../../infrastructure/ShippingClient';
import type { FormData } from './components';
import {
  ShippingList,
  CreateShippingPolicyModal,
  EditShippingPolicyModal,
  DeleteShippingPolicyModal,
  GlobalShippingSettingsCard,
} from './components';

const ShippingPage = () => {
  const {
    policies,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    createError,
    deleteError,
    createOrUpdatePolicy,
    deletePolicy,
    refreshPolicies,
  } = useShipping();

  const {
    settings: globalSettings,
    isLoading: isLoadingGlobal,
    isUpdating: isUpdatingGlobal,
    error: globalError,
    updateError: globalUpdateError,
    updateSettings: updateGlobalSettings,
    refreshSettings: refreshGlobalSettings,
  } = useGlobalShippingSettings();

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<ShippingPolicy | null>(null);

  // Handle create policy
  const handleCreatePolicy = async (data: FormData) => {
    try {
      await createOrUpdatePolicy(data);
      setShowCreateModal(false);
    } catch (error) {
      // Error is handled by the hook
    }
  };

  // Handle edit policy
  const handleEditClick = (policy: ShippingPolicy) => {
    setSelectedPolicy(policy);
    setShowEditModal(true);
  };

  const handleEditPolicy = async (data: FormData) => {
    try {
      await createOrUpdatePolicy(data);
      setShowEditModal(false);
      setSelectedPolicy(null);
    } catch (error) {
      // Error is handled by the hook
    }
  };

  // Handle delete policy
  const handleDeleteClick = (policy: ShippingPolicy) => {
    setSelectedPolicy(policy);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPolicy) {
      return;
    }

    try {
      await deletePolicy(selectedPolicy.countryCode);
      setShowDeleteModal(false);
      setSelectedPolicy(null);
    } catch (error) {
      // Error is handled by the hook
    }
  };

  // Handle modal close
  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedPolicy(null);
  };

  // Handle global settings update
  const handleUpdateGlobalSettings = async (settings: GlobalShippingSettings) => {
    await updateGlobalSettings(settings);
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>Shipping Policy Management</h2>
              <p className="text-muted mb-0">Manage shipping policies for different countries and regions</p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                Create New Policy
              </Button>
            </div>
          </div>

          {/* Error alerts */}
          {error && (
            <Alert variant="danger" className="mb-3">
              <Alert.Heading>Failed to load shipping policies</Alert.Heading>
              <p>{error.message}</p>
              <Button variant="outline-danger" size="sm" onClick={refreshPolicies}>
                Retry
              </Button>
            </Alert>
          )}

          {createError && (
            <Alert variant="danger" className="mb-3">
              <Alert.Heading>Failed to create/update policy</Alert.Heading>
              <p>{createError.message}</p>
            </Alert>
          )}

          {deleteError && (
            <Alert variant="danger" className="mb-3">
              <Alert.Heading>Failed to delete policy</Alert.Heading>
              <p>{deleteError.message}</p>
            </Alert>
          )}

          {/* Global Shipping Settings */}
          <GlobalShippingSettingsCard
            settings={globalSettings}
            isLoading={isLoadingGlobal}
            isUpdating={isUpdatingGlobal}
            error={globalError}
            updateError={globalUpdateError}
            onUpdate={handleUpdateGlobalSettings}
            onRefresh={refreshGlobalSettings}
          />

          {/* Policies table */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Shipping Policies</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <ShippingList
                policies={policies}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                isLoading={isLoading}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      <CreateShippingPolicyModal
        show={showCreateModal}
        onHide={handleCloseModals}
        onSubmit={handleCreatePolicy}
        isLoading={isCreating}
      />

      <EditShippingPolicyModal
        show={showEditModal}
        onHide={handleCloseModals}
        onSubmit={handleEditPolicy}
        policy={selectedPolicy}
        isLoading={isUpdating}
      />

      <DeleteShippingPolicyModal
        show={showDeleteModal}
        onHide={handleCloseModals}
        onConfirm={handleDeleteConfirm}
        policy={selectedPolicy}
        isLoading={isDeleting}
      />
    </Container>
  );
};

export default ShippingPage;
